import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { SingleSelectFilterField } from '@/ui/composites/filters/SingleSelectFilterField';
import { Popover } from '@/ui/primitives';
import type { ReactElement } from 'react';

export type TableSortOption<TSortKey extends string> = { key: TSortKey; label: string };
const popoverFontSize = 15;

type TableSortPopoverProps<TSortKey extends string> = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  sortKey: TSortKey;
  sortDirection: 'asc' | 'desc';
  sortOptions: Array<TableSortOption<TSortKey>>;
  onSortChange: (sortKey: TSortKey, sortDirection: 'asc' | 'desc') => void;
  preserveWindowScroll: (action: () => void) => void;
};

export const TableSortPopover = <TSortKey extends string>({
  open,
  anchorEl,
  onClose,
  sortKey,
  sortDirection,
  sortOptions,
  onSortChange,
  preserveWindowScroll,
}: TableSortPopoverProps<TSortKey>): ReactElement => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    paperSx={{
      backgroundColor: 'var(--surface-page)',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: `${r('md')} !important`,
      overflow: 'hidden !important',
    }}
  >
    <Stack gap="none">
      <Stack gap="md" sx={{ pl: s('xl'), pr: s('lg'), py: s('lg') }}>
        <SingleSelectFilterField
          label="Sort By"
          labelTransform="none"
          fontSizePx={popoverFontSize}
          labelWidthPx={84}
          labelControlGap={s('md')}
          inputHeight={32}
          inputAriaLabel="Sort by field"
          value={sortKey}
          options={sortOptions.map((option) => ({ value: option.key, label: option.label }))}
          onChange={(nextSortKey) => {
            preserveWindowScroll(() => {
              onSortChange(nextSortKey as TSortKey, sortDirection);
            });
          }}
          controlWidthPx={156}
          inputBackgroundColor="background.default"
          inputLeftPadding={s('xs')}
        />
        <SingleSelectFilterField
          label="Direction"
          labelTransform="none"
          fontSizePx={popoverFontSize}
          labelWidthPx={84}
          labelControlGap={s('md')}
          inputHeight={32}
          inputAriaLabel="Sort direction"
          value={sortDirection}
          options={[
            { value: 'asc', label: 'Ascending' },
            { value: 'desc', label: 'Descending' },
          ]}
          onChange={(nextSortDirection) => {
            preserveWindowScroll(() => {
              onSortChange(sortKey, nextSortDirection as 'asc' | 'desc');
            });
          }}
          controlWidthPx={156}
          inputBackgroundColor="background.default"
          inputLeftPadding={s('xs')}
        />
      </Stack>
    </Stack>
  </Popover>
);
